import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import { ref } from 'vue'
import { importScrollTrigger } from './gsap'
import SwupPreloadPlugin from '@swup/preload-plugin'
import SwupScrollPlugin from '@swup/scroll-plugin'

const st = importScrollTrigger()

const isVisiting = ref(false)

export const swup = new Swup({
    containers: ['#swup'],
    animationSelector: '[class*="page-transition-"]',
    plugins: [
        new SwupHeadPlugin({
            persistAssets: true,
        }),
        new SwupPreloadPlugin(),
        new SwupScrollPlugin(),
    ],})

// The target section (e.g. #upcoming-events) is mounted late by block
// initialisation, so on a direct page load the browser's native hash scroll
// fires before the element exists. Wait for it, then scroll.
function waitForElement(selector: string, timeout = 5000): Promise<Element | null> {
    const existing = document.querySelector(selector)
    if (existing) return Promise.resolve(existing)

    return new Promise((resolve) => {
        const obs = new MutationObserver(() => {
            const el = document.querySelector(selector)
            if (el) {
                obs.disconnect()
                resolve(el)
            }
        })
        obs.observe(document.documentElement, { childList: true, subtree: true })
        setTimeout(() => {
            obs.disconnect()
            resolve(document.querySelector(selector))
        }, timeout)
    })
}

// Wait for the (late-mounted) anchor target, then scroll to it. We scroll
// instantly rather than smoothly: ScrollTrigger repeatedly resets and restores
// the scroll position while it refreshes, and a smooth scroll is still mid-flight
// (near the top) when ScrollTrigger captures the position, so it gets restored to
// 0. Setting it synchronously means the correct position is captured and preserved.
function scrollToHash(hash: string): Promise<Element | null> {
    return waitForElement(hash).then((el) => {
        if (el) {
            el.scrollIntoView({ block: 'start', behavior: 'instant' })
            st.refresh()
        }
        return el
    })
}

// Direct page load on an anchor: the browser's native hash scroll fires before
// the target exists, so handle it ourselves.
if (window.location.hash) {
    scrollToHash(window.location.hash).then((el) => {
        // Tell the navbar we just jumped to a deep anchor so it can settle into
        // its hidden state instead of staying shown (see use-nav-context).
        if (el) window.dispatchEvent(new CustomEvent('bnm:anchor-scrolled'))
    })
}

swup.hooks.on('visit:start', (e) => {
    isVisiting.value = true

    if (e.scroll.reset) {
        window.scrollTo({ top: 0, behavior: 'instant' })
    }
})

swup.hooks.on('visit:end', (e) => {
    isVisiting.value = false
    st.refresh()

    if (e.to.hash) {
        scrollToHash(e.to.hash)
    }
})

export const useSwup = () => {
    return {
        isVisiting,
    }
}
